import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

type WizardData = {
  // Step 1: Entity
  legal_name: string;
  state: string;
  formation_date: string;
  filing_id: string;

  // Step 2: EIN
  ein: string;
  ein_status: 'pending' | 'issued';
  ein_issued_date: string;

  // Step 3: Ownership
  structure: 'single_member' | 'multi_member';
  owner_type: 'foreign_person' | 'us_person' | 'foreign_entity' | 'us_entity';
  owner_name: string;
  owner_country: string;
  owner_ftin: string;

  // Step 4: Registered Agent
  registered_agent_name: string;
  registered_agent_provider: string;
  registered_agent_renewal_date: string;
  registered_agent_annual_cost: string;
  mailing_address: string;

  // Step 5: Operations
  business_model: string;
  target_markets: string[];
  has_us_customers: boolean;
  has_employees: boolean;
  has_contractors: boolean;

  // Step 6: Banking
  bank_provider: string;
  bank_account_opened_date: string;
};

const initialData: WizardData = {
  legal_name: 'ILU LLC',
  state: 'WY',
  formation_date: '',
  filing_id: '',
  ein: '',
  ein_status: 'pending',
  ein_issued_date: '',
  structure: 'single_member',
  owner_type: 'foreign_person',
  owner_name: '',
  owner_country: 'MX',
  owner_ftin: '',
  registered_agent_name: '',
  registered_agent_provider: '',
  registered_agent_renewal_date: '',
  registered_agent_annual_cost: '50',
  mailing_address: '',
  business_model: 'saas',
  target_markets: ['LATAM'],
  has_us_customers: false,
  has_employees: false,
  has_contractors: false,
  bank_provider: 'mercury',
  bank_account_opened_date: '',
};

export function OnboardingWizard() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<WizardData>(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const totalSteps = 6;

  function update<K extends keyof WizardData>(field: K, value: WizardData[K]) {
    setData((d) => ({ ...d, [field]: value }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        legal_name: data.legal_name,
        state: data.state,
        formation_date: data.formation_date,
        filing_id: data.filing_id || null,
        ein: data.ein || null,
        ein_status: data.ein_status,
        ein_issued_date: data.ein_issued_date || null,
        structure: data.structure,
        owner_type: data.owner_type,
        owner_name: data.owner_name || null,
        owner_country: data.owner_country || null,
        owner_ftin: data.owner_ftin || null,
        registered_agent_name: data.registered_agent_name || null,
        registered_agent_provider: data.registered_agent_provider || null,
        registered_agent_renewal_date: data.registered_agent_renewal_date || null,
        registered_agent_annual_cost: data.registered_agent_annual_cost
          ? parseFloat(data.registered_agent_annual_cost)
          : null,
        mailing_address: data.mailing_address || null,
        business_model: data.business_model,
        target_markets: JSON.stringify(data.target_markets),
        has_us_customers: data.has_us_customers ? 1 : 0,
        has_employees: data.has_employees ? 1 : 0,
        has_contractors: data.has_contractors ? 1 : 0,
        bank_provider: data.bank_provider || null,
        bank_account_opened_date: data.bank_account_opened_date || null,
      };

      const res = await api.entities.create(payload);
      // Store entity id in localStorage for subsequent navigation
      localStorage.setItem('ilu_entity_id', res.id);
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear entidad');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '4rem 2rem' }}>
      {/* Progress */}
      <div style={{ marginBottom: '3rem' }}>
        <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>
          Paso {step} de {totalSteps}
        </div>
        <div
          style={{
            height: 2,
            background: 'var(--border)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${(step / totalSteps) * 100}%`,
              background: 'var(--accent)',
              transition: 'width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
          />
        </div>
      </div>

      <div key={step} className="fade-up">
        {step === 1 && <StepEntity data={data} update={update} />}
        {step === 2 && <StepEIN data={data} update={update} />}
        {step === 3 && <StepOwnership data={data} update={update} />}
        {step === 4 && <StepRA data={data} update={update} />}
        {step === 5 && <StepOperations data={data} update={update} />}
        {step === 6 && <StepBanking data={data} update={update} />}
      </div>

      {error && (
        <div
          style={{
            marginTop: '2rem',
            padding: '1rem',
            border: '1px solid var(--danger)',
            color: 'var(--danger-bright)',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Navigation */}
      <div
        className="flex-between"
        style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--border)' }}
      >
        <button
          className="btn btn-ghost"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
        >
          ← Atrás
        </button>
        {step < totalSteps ? (
          <button className="btn btn-primary" onClick={() => setStep((s) => s + 1)}>
            Continuar →
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creando…' : 'Crear mi compliance map'}
          </button>
        )}
      </div>
    </div>
  );
}

// ================================================================
// Step Components
// ================================================================

type StepProps = {
  data: WizardData;
  update: <K extends keyof WizardData>(field: K, value: WizardData[K]) => void;
};

function StepHeader({ eyebrow, title, description }: { eyebrow: string; title: React.ReactNode; description: string }) {
  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>{eyebrow}</div>
      <h1 style={{ marginBottom: '1rem' }}>{title}</h1>
      <p style={{ color: 'var(--text-dim)', lineHeight: 1.6, maxWidth: '55ch' }}>{description}</p>
    </div>
  );
}

function InlineLearning({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        borderLeft: '2px solid var(--accent)',
        background: 'var(--bg-raised)',
        fontSize: '0.85rem',
        lineHeight: 1.7,
        color: 'var(--text-dim)',
      }}
    >
      <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>Por qué lo preguntamos</div>
      {children}
    </div>
  );
}

function StepEntity({ data, update }: StepProps) {
  return (
    <>
      <StepHeader
        eyebrow="Paso 1 · Entidad"
        title={<>Empecemos por <em style={{ color: 'var(--accent)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>la LLC</em></>}
        description="Datos básicos de registro de tu LLC en Wyoming. Esta información define los vencimientos de todos los compliance items que calcularemos automáticamente."
      />
      <div className="stack-md">
        <div>
          <label>Nombre legal</label>
          <input type="text" value={data.legal_name} onChange={(e) => update('legal_name', e.target.value)} placeholder="ILU LLC" />
        </div>
        <div>
          <label>Estado de formación</label>
          <select value={data.state} onChange={(e) => update('state', e.target.value)}>
            <option value="WY">Wyoming</option>
            <option value="DE">Delaware</option>
            <option value="NM">New Mexico</option>
            <option value="FL">Florida</option>
            <option value="TX">Texas</option>
          </select>
        </div>
        <div>
          <label>Fecha de formación</label>
          <input type="date" value={data.formation_date} onChange={(e) => update('formation_date', e.target.value)} />
        </div>
        <div>
          <label>Secretary of State Filing ID (opcional)</label>
          <input type="text" className="mono" value={data.filing_id} onChange={(e) => update('filing_id', e.target.value)} placeholder="2025-001234567" />
        </div>
      </div>
      <InlineLearning>
        La fecha de formación es el input más importante: determina (1) cuándo vence tu primer Form 5472 — el 15 de abril del año siguiente, y (2) qué día de qué mes debes presentar el Wyoming Annual Report cada año.
      </InlineLearning>
    </>
  );
}

function StepEIN({ data, update }: StepProps) {
  return (
    <>
      <StepHeader
        eyebrow="Paso 2 · IRS"
        title={<>Tu <em style={{ color: 'var(--accent)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>EIN</em>, la llave del sistema</>}
        description="El EIN es el RFC de tu LLC en Estados Unidos. Sin él no puedes filear Form 5472, abrir banco, ni integrar pasarelas de pago."
      />
      <div className="stack-md">
        <div>
          <label>Status</label>
          <select value={data.ein_status} onChange={(e) => update('ein_status', e.target.value as any)}>
            <option value="pending">En trámite</option>
            <option value="issued">Ya emitido</option>
          </select>
        </div>
        {data.ein_status === 'issued' && (
          <>
            <div>
              <label>Número EIN</label>
              <input
                type="text"
                className="mono"
                value={data.ein}
                onChange={(e) => update('ein', e.target.value)}
                placeholder="XX-XXXXXXX"
                maxLength={10}
              />
            </div>
            <div>
              <label>Fecha de emisión</label>
              <input type="date" value={data.ein_issued_date} onChange={(e) => update('ein_issued_date', e.target.value)} />
            </div>
          </>
        )}
      </div>
      <InlineLearning>
        {data.ein_status === 'pending' ? (
          <>
            Si estás en trámite al 15 de abril, tu primer Form 5472 no se puede filear a tiempo. La solución es presentar Form 7004 antes del 15 de abril para extender el plazo hasta el 15 de octubre. El sistema te creará esa obligación automáticamente.
          </>
        ) : (
          <>
            Perfecto. Con EIN emitido puedes filear Form 5472 directamente. Asegúrate de tener el documento <span className="mono">CP 575</span> (la carta de confirmación del IRS) guardado — es tu comprobante oficial.
          </>
        )}
      </InlineLearning>
    </>
  );
}

function StepOwnership({ data, update }: StepProps) {
  return (
    <>
      <StepHeader
        eyebrow="Paso 3 · Ownership"
        title={<>Estructura de <em style={{ color: 'var(--accent)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>propiedad</em></>}
        description="Define quién es el dueño de la LLC. Esta combinación (single-member + foreign owner) activa la obligación de Form 5472."
      />
      <div className="stack-md">
        <div>
          <label>Estructura</label>
          <select value={data.structure} onChange={(e) => update('structure', e.target.value as any)}>
            <option value="single_member">Single-Member (un solo dueño)</option>
            <option value="multi_member">Multi-Member (varios dueños)</option>
          </select>
        </div>
        <div>
          <label>Tipo de dueño</label>
          <select value={data.owner_type} onChange={(e) => update('owner_type', e.target.value as any)}>
            <option value="foreign_person">Persona física no residente de EEUU</option>
            <option value="us_person">Persona física residente de EEUU</option>
            <option value="foreign_entity">Entidad extranjera</option>
            <option value="us_entity">Entidad estadounidense</option>
          </select>
        </div>
        <div>
          <label>Nombre del dueño</label>
          <input type="text" value={data.owner_name} onChange={(e) => update('owner_name', e.target.value)} />
        </div>
        <div>
          <label>País de residencia fiscal</label>
          <input type="text" value={data.owner_country} onChange={(e) => update('owner_country', e.target.value)} placeholder="MX" maxLength={2} className="mono" />
        </div>
        <div>
          <label>FTIN (Foreign Taxpayer ID)</label>
          <input type="text" className="mono" value={data.owner_ftin} onChange={(e) => update('owner_ftin', e.target.value)} placeholder="Tu RFC si eres mexicano" />
        </div>
      </div>
      {data.structure === 'single_member' && data.owner_type === 'foreign_person' && (
        <InlineLearning>
          Esta combinación es la que activa la obligación de Form 5472 cada año. Desde la reforma TCJA 2017, TODAS las single-member LLCs con foreign owner deben filear, aunque no tengan actividad. La OBBBA 2026 agrega el requisito de incluir tu FTIN en los filings.
        </InlineLearning>
      )}
    </>
  );
}

function StepRA({ data, update }: StepProps) {
  return (
    <>
      <StepHeader
        eyebrow="Paso 4 · Registered Agent"
        title={<>Tu <em style={{ color: 'var(--accent)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>representante</em> en Wyoming</>}
        description="Wyoming exige que toda LLC tenga un Registered Agent con dirección física en el estado. Este recibe tus notices del IRS y del estado."
      />
      <div className="stack-md">
        <div>
          <label>Nombre del Registered Agent</label>
          <input type="text" value={data.registered_agent_name} onChange={(e) => update('registered_agent_name', e.target.value)} />
        </div>
        <div>
          <label>Proveedor</label>
          <select value={data.registered_agent_provider} onChange={(e) => update('registered_agent_provider', e.target.value)}>
            <option value="">Seleccionar…</option>
            <option value="Wyoming Agents">Wyoming Agents ($50/año)</option>
            <option value="Northwest Registered Agent">Northwest Registered Agent ($125/año)</option>
            <option value="Registered Agents Inc">Registered Agents Inc ($200/año)</option>
            <option value="Otro">Otro</option>
          </select>
        </div>
        <div>
          <label>Fecha de renovación del servicio</label>
          <input type="date" value={data.registered_agent_renewal_date} onChange={(e) => update('registered_agent_renewal_date', e.target.value)} />
        </div>
        <div>
          <label>Costo anual (USD)</label>
          <input type="number" className="mono" value={data.registered_agent_annual_cost} onChange={(e) => update('registered_agent_annual_cost', e.target.value)} />
        </div>
        <div>
          <label>Dirección de correspondencia (mailing)</label>
          <textarea rows={2} value={data.mailing_address} onChange={(e) => update('mailing_address', e.target.value)} />
        </div>
      </div>
      <InlineLearning>
        Si dejas vencer el RA, cualquier demanda o IRS notice se considera entregada aunque no la hayas recibido — la LLC puede perder casos por default judgment. Marca la fecha de renovación con tiempo; el sistema te alertará 30 días antes.
      </InlineLearning>
    </>
  );
}

function StepOperations({ data, update }: StepProps) {
  const toggleMarket = (m: string) => {
    const markets = data.target_markets.includes(m)
      ? data.target_markets.filter((x) => x !== m)
      : [...data.target_markets, m];
    update('target_markets', markets);
  };

  return (
    <>
      <StepHeader
        eyebrow="Paso 5 · Operación"
        title={<>¿Cómo <em style={{ color: 'var(--accent)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>opera</em> la LLC?</>}
        description="Define el modelo de negocio y los mercados. Esto determina si debemos monitorear sales tax multi-estado, 1099s, y otros compliance items."
      />
      <div className="stack-md">
        <div>
          <label>Modelo de negocio</label>
          <select value={data.business_model} onChange={(e) => update('business_model', e.target.value)}>
            <option value="saas">SaaS / Software</option>
            <option value="services">Servicios profesionales</option>
            <option value="ecommerce">E-commerce</option>
            <option value="holding">Holding</option>
            <option value="other">Otro</option>
          </select>
        </div>
        <div>
          <label>Mercados objetivo</label>
          <div className="flex" style={{ flexWrap: 'wrap' }}>
            {['MX', 'LATAM', 'US', 'EU', 'Global'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => toggleMarket(m)}
                className="btn btn-ghost"
                style={{
                  borderColor: data.target_markets.includes(m) ? 'var(--accent)' : 'var(--border)',
                  color: data.target_markets.includes(m) ? 'var(--accent)' : 'var(--text-dim)',
                }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="stack-sm">
          <label>
            <input
              type="checkbox"
              checked={data.has_us_customers}
              onChange={(e) => update('has_us_customers', e.target.checked)}
              style={{ marginRight: '0.5rem', width: 'auto' }}
            />
            Tenemos clientes en Estados Unidos
          </label>
          <label>
            <input
              type="checkbox"
              checked={data.has_employees}
              onChange={(e) => update('has_employees', e.target.checked)}
              style={{ marginRight: '0.5rem', width: 'auto' }}
            />
            Tenemos empleados (W-2)
          </label>
          <label>
            <input
              type="checkbox"
              checked={data.has_contractors}
              onChange={(e) => update('has_contractors', e.target.checked)}
              style={{ marginRight: '0.5rem', width: 'auto' }}
            />
            Tenemos contractors (1099)
          </label>
        </div>
      </div>
      <InlineLearning>
        {data.has_us_customers
          ? 'Con clientes en USA, debemos monitorear economic nexus por estado (típicamente $100K o 200 transacciones). Te crearemos un compliance item de revisión trimestral.'
          : 'Sin clientes en USA, no tienes sales tax nexus en ningún estado. El compliance se simplifica mucho.'}
      </InlineLearning>
    </>
  );
}

function StepBanking({ data, update }: StepProps) {
  return (
    <>
      <StepHeader
        eyebrow="Paso 6 · Banca"
        title={<>Cuenta <em style={{ color: 'var(--accent)', fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>bancaria</em> USA</>}
        description="El proveedor bancario importa para evaluar el 1% Remittance Tax de OBBBA 2026. Bancos tradicionales están exentos; cash-based providers no."
      />
      <div className="stack-md">
        <div>
          <label>Proveedor bancario</label>
          <select value={data.bank_provider} onChange={(e) => update('bank_provider', e.target.value)}>
            <option value="mercury">Mercury</option>
            <option value="wise">Wise Business</option>
            <option value="relay">Relay</option>
            <option value="brex">Brex</option>
            <option value="traditional">Banco tradicional (Chase, BofA, etc.)</option>
            <option value="none">Aún no tengo</option>
          </select>
        </div>
        {data.bank_provider !== 'none' && (
          <div>
            <label>Fecha de apertura</label>
            <input type="date" value={data.bank_account_opened_date} onChange={(e) => update('bank_account_opened_date', e.target.value)} />
          </div>
        )}
      </div>
      <InlineLearning>
        {data.bank_provider === 'none'
          ? 'Para abrir cuenta bancaria necesitas tu EIN. Mercury es el estándar para founders; tarda ~1 semana en aprobarse.'
          : 'Tu proveedor está sujeto al Bank Secrecy Act, así que tus transferencias ACH/wire al extranjero están EXENTAS del 1% remittance tax. Buena noticia.'}
      </InlineLearning>

      <div style={{ marginTop: '3rem', padding: '2rem', border: '1px solid var(--accent)', background: 'rgba(200,161,90,0.05)' }}>
        <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: '1rem' }}>
          Al terminar
        </div>
        <p style={{ color: 'var(--text)', lineHeight: 1.7 }}>
          Generaremos automáticamente tu <strong>Compliance Map personalizado</strong>: todas las
          obligaciones federales, de Wyoming y multi-estado, con fechas exactas, costos y multas
          calculadas específicamente para ILU LLC.
        </p>
      </div>
    </>
  );
}
